import React from 'react';

import { Box, FormControlLabel, Theme, Typography, useTheme } from '@material-ui/core';

import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { StaffResourceOption } from '@vyce/core/src/utils/staffPermissions';

import { useRolesStyles } from '../styles';

interface Props {
  label?: string;
  resources: StaffResourceOption[];
  selectedIds: string[];
  disabled?: boolean;
  errorText?: string;
  onToggle: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  /** Right-hand slot of a resource row — the per-resource exception badge/toggle. */
  renderItemRight?: (resourceId: string) => React.ReactNode;
  /** Expanded content under a resource row — the per-resource permission list. */
  renderItemDetail?: (resourceId: string) => React.ReactNode;
}

export const ResourcePicker: React.FC<Props> = ({
  label,
  resources,
  selectedIds,
  disabled,
  errorText,
  onToggle,
  onSelectAll,
  renderItemRight,
  renderItemDetail,
}) => {
  const theme = useTheme<Theme>();
  const classes = useRolesStyles();
  // A wrapped layout can't host expandable rows, so switch to a list when the
  // per-resource slots are in play.
  const asList = !!renderItemRight || !!renderItemDetail;

  return (
    <Box>
      {!!label && <Typography variant="body2">{label}</Typography>}
      {resources.length > 1 && (
        <Box marginTop={0.5} paddingBottom={0.5} borderBottom={`1px solid ${theme.palette.divider}`}>
          <FormControlLabel
            control={
              <AppCheckbox
                variant="rectangle"
                color="primary"
                disabled={disabled}
                checked={selectedIds.length === resources.length}
                onChange={e => onSelectAll(e.target.checked)}
              />
            }
            label="Select All"
          />
        </Box>
      )}
      <Box display="flex" flexDirection={asList ? 'column' : 'row'} flexWrap="wrap" marginTop={0.5}>
        {resources.map(item => (
          <React.Fragment key={item.id}>
            <div className={asList ? classes.resourceRow : undefined}>
              <FormControlLabel
                className={asList ? classes.resourceRowLabel : undefined}
                style={asList ? undefined : { marginRight: 20 }}
                control={
                  <AppCheckbox
                    variant="rectangle"
                    color="primary"
                    disabled={disabled}
                    checked={selectedIds.includes(item.id)}
                    onChange={e => onToggle(item.id, e.target.checked)}
                  />
                }
                label={item.name}
              />
              {renderItemRight?.(item.id)}
            </div>
            {renderItemDetail?.(item.id)}
          </React.Fragment>
        ))}
      </Box>
      {!!errorText && <div className={classes.scopeError}>{errorText}</div>}
    </Box>
  );
};
